import * as _ from 'lodash';
import * as hash from 'object-hash';
import { Component, FormEvent } from 'react';
import { connect, Dispatch } from 'react-redux';
import { AppActionCreators, CenterActionCreators, IApplicationState, ICenterLabDataResponse } from '@nkr/store/index';
import { ILabData, IListItem, IUpdateLabDataRequest } from '@nkr/models/index';
import { Col, ControlLabel, FormControl, FormGroup, Radio, Row } from 'react-bootstrap';
import CenterProfileNav from '../edit-nav';
import CrossMatchTubes from './cross-match-tubes';
import TransportTubes from './transport-tubes';
import { FormWrapper } from '../styles';
import { IErrorItem } from '../../error-item';
import { initialLabData } from '../initial-data';
import { profileNavKey } from '../../profile-nav-key';
import { toast } from 'react-toastify';

interface IProps {
    centerId: number,
    isAdmin: boolean,
    isAdminUser: boolean,
    hasDataChanges: boolean,
    xmVirtualOptionList: IListItem[],
    requiredTubeList: IListItem[],
    onTabClick: (href: string) => void,
    onDataChanged: (hasDataChanged: boolean) => void,
    updateErrors: (list: IErrorItem[]) => void
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getData: (centerId: number) => Promise<ICenterLabDataResponse>,
    updateData: (data: IUpdateLabDataRequest) => Promise<ICenterLabDataResponse>,
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    data: ILabData,
    hash: string,
    isDirty: boolean,
    isValid: boolean
}

class CenterLabDataTab extends Component<TProps, IState> {
    readonly state: IState = {
        data: initialLabData,
        hash: '',
        isDirty: false,
        isValid: true
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting lab data, one moment please...').then(() => {
            this.props.getData(this.props.centerId).then(response => {
                this.setState({
                    data: response.data.results,
                    hash: hash(response.data.results)
                });

                this.props.hideLoading();
            });
        });
    }

    handleSave = () => {
        let {
            hasFrozenScreening, hasFrozenExploratory, xmVirtualOption,
            labLavender40, labLavender70, labLavender85, labLavender100, labPink60, labPink100, labRed100, labRed70, labYellow85, labGreen100,
            transLavender40, transLavender70, transLavender85, transLavender100, transPink60, transPink100, transRed100, transRed70, transYellow85, transGreen100
        } = this.state.data;

        let data: IUpdateLabDataRequest = {
            id: this.props.centerId,
            hasFrozenScreening,
            hasFrozenExploratory,
            xmVirtualOption,
            labLavender40,
            labLavender70,
            labLavender85,
            labLavender100,
            labPink60,
            labPink100,
            labRed100,
            labRed70,
            labYellow85,
            labGreen100,
            transLavender40,
            transLavender70,
            transLavender85,
            transLavender100,
            transPink60,
            transPink100,
            transRed100,
            transRed70,
            transYellow85,
            transGreen100
        };

        //count the cross match tubes and if it's above 8, throw an error
        let crossMatchTubeCount = 0;
        Object.keys(data)
            .filter((key, index) => key.includes('lab'))
                .forEach(x => 
                    { 
                        let val = data[x];
                        if (val > -1) {
                            crossMatchTubeCount += val; 
                        }
                    })

        if (crossMatchTubeCount > 8){
            toast.error('The Cross Match Tube count is greater than the allowed maximum, which is 8. Please reconfigure the Cross Match Tube counts');
            return;
        }

        this.props.showLoading('Saving center LAB data, one moment please...').then(() => {
            this.props.updateData(data).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    this.setState({
                        data: response.data.results,
                        hash: hash(response.data.results),
                    }, () => {
                        toast.success('Center Lab data updated successfully');

                        this.props.onDataChanged(false);
                    });
                }
            });
        });
    }

    handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let id = e.currentTarget.id;
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let data = _.cloneDeep(this.state.data);

        if (name == 'hasFrozenExploratory') {
            data.hasFrozenExploratory = parseInt(value) == 1;
        } else if (name == 'hasFrozenScreening') {
            data.hasFrozenScreening = parseInt(value) == 1;
        } else if (name == 'xmVirtualOption') {
            data.xmVirtualOption = parseInt(value);
        } else {
            data[id] = parseInt(value);
        }

        let newHash = hash(data);

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(newHash != this.state.hash);
        });
    }

    render = () => {
        const { hasFrozenExploratory, hasFrozenScreening, labLavender85, labLavender40, labLavender70, labLavender100,
            labPink60, labPink100,
            labRed70, labRed100,
            labGreen100,
            labYellow85,
            transLavender85, transLavender40, transLavender70, transLavender100,
            transPink60, transPink100,
            transRed70, transRed100,
            transGreen100,
            transYellow85,
            xmVirtualOption } = this.state.data;

        return (
            <>
                <CenterProfileNav
                    activeKey={profileNavKey.LAB}
                    isAdmin={this.props.isAdmin}
                    isAdminUser={this.props.isAdminUser}
                    centerId={this.props.centerId}
                    hasChanges={this.props.hasDataChanges}
                    onTabClick={this.props.onTabClick}
                    onSave={this.handleSave}
                />

                <div style={{ padding: '20px 10px', borderWidth: '1px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ddd', borderRadius: 4 }}>
                    <Row>
                        <Col xs={12} lg={10} lgOffset={1}>
                            <CrossMatchTubes
                                requiredTubeList={this.props.requiredTubeList}
                                labLavender85={labLavender85}
                                labLavender40={labLavender40}
                                labLavender70={labLavender70}
                                labLavender100={labLavender100}
                                labPink60={labPink60}
                                labPink100={labPink100}
                                labRed70={labRed70}
                                labRed100={labRed100}
                                labGreen100={labGreen100}
                                labYellow85={labYellow85}
                                onChange={this.handleFieldChange}
                            />

                            <TransportTubes
                                requiredTubeList={this.props.requiredTubeList}
                                transLavender85={transLavender85}
                                transLavender40={transLavender40}
                                transLavender70={transLavender70}
                                transLavender100={transLavender100}
                                transPink60={transPink60}
                                transPink100={transPink100}
                                transRed70={transRed70}
                                transRed100={transRed100}
                                transGreen100={transGreen100}
                                transYellow85={transYellow85}
                                onChange={this.handleFieldChange}
                            />
                        </Col>
                    </Row>
                </div>
            </>
        );
    }

}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {}
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getData: (centerId) => {
            return dispatch(CenterActionCreators.getLabData(centerId));
        },
        updateData: (data) => {
            return dispatch(CenterActionCreators.updateLabData(data));
        },
    }
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterLabDataTab);
